<template>
<section class="lidar-chart-wrapper">
  <section
    :style="{
      width: `${chartWidth}px`,
      height: `${chartHeight}px`
    }"
    class="lidar-chart-inner"
    @mousedown="onChartMouseDown"
    @mousemove="onChartMouseMove"
    @mouseup="onChartMouseUp"
    @mouseout="onChartMouseOut"
    @mousewheel="onChartMouseWheel"
    @dblclick="onChartDBLClick"
    @contextmenu.prevent="onChartContextMenu"
  >
    <canvas ref="lidar-chart" />
    <canvas ref="tooltip-canvas" class="tooltip-canvas" />
    <canvas ref="selected-canvas" class="selected-canvas" />
  </section>

  <a class="download-btn" @click="onDownloadBtnClick">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      style="fill:currentColor;"
      viewBox="0 0 1024 1024"
      version="1.1"
    >
      <path d="M908.304 900.882H113.648c-11.895 0-21.535 9.64-21.535 21.535 0 11.896 9.64 21.536 21.535 21.536h794.655c11.896 0 21.536-9.64 21.536-21.536 0.001-11.894-9.64-21.535-21.535-21.535z m-412.575-68.92a21.106 21.106 0 0 0 3.315 2.71c0.493 0.328 1.036 0.555 1.553 0.845 0.716 0.387 1.397 0.818 2.152 1.131 0.698 0.282 1.412 0.455 2.128 0.661 0.63 0.18 1.232 0.43 1.89 0.559 1.393 0.267 2.797 0.422 4.21 0.422 1.413 0 2.825-0.155 4.215-0.422 0.653-0.136 1.256-0.379 1.894-0.559 0.714-0.206 1.438-0.379 2.127-0.661 0.757-0.312 1.446-0.744 2.162-1.138 0.508-0.283 1.042-0.51 1.544-0.838a22.044 22.044 0 0 0 3.315-2.71l326.168-326.17c8.416-8.416 8.416-22.04 0-30.449-8.409-8.417-22.053-8.417-30.454 0L532.512 764.777V136.375c0-11.895-9.64-21.535-21.535-21.535-11.896 0-21.537 9.64-21.537 21.535v628.394L200.005 475.335c-8.408-8.417-22.052-8.417-30.452 0-8.417 8.417-8.417 22.04 0 30.45L495.729 831.96z m0 0"/>
    </svg>
  </a>

  <section
    :style="{
        top: `${tooltip.position.y}px`,
        left: `${tooltip.position.x}px`,
        visibility: tooltip.visibility
      }"
    class="tooltip"
  >
    <div>
      <span :style="{
        'background-color': `rgb(${tooltip.legendColor.join(',')})`
      }" class="legend-marker"></span>
      <span>{{tooltip.dataName}}</span>
    </div>
    <div
      v-for="(tooltipData, index) in tooltip.data"
      :key="index"
    >
      <span class="tooltip-label">{{tooltipData.label}}: </span>
      <span class="tooltip-value">{{tooltipData.value}}</span>
    </div>
  </section>
</section>
</template>

<script>
import merge from 'deepmerge'
import {
  LIDAR_CHART_DATA_CANCEL_SELECT,
  LIDAR_CHART_DATA_SELECTED,
  LIDAR_CHART_DESTROY, LIDAR_CHART_MOVED,
  LIDAR_CHART_READY
} from './EventKeys'
import {
  AXIS_FONT,
  BORDER_STYLE,
  FONT_SIZE,
  LEGEND_BORDER_COLOR, LINE_WIDTH,
  MAIN_AXIS_SIZE, SPLIT_LINE_STYLE,
  SUB_AXIS_SIZE,
  TEXT_FILL_STYLE, TOOLTIP_BORDER_STYLE, VALUE_INTERVAL
} from '../chartStyle'

const [STD_WIDTH_SCALE, STD_HEIGHT_SCALE] = [2, 1]

const PER_COLOR_STEP = 60

export default {
  name: 'LidarChart',
  data () {
    return {
      chartWidth: 0,
      chartHeight: 0,
      yAxisWidth: 51,

      startX: 0,
      startY: 0,

      xAxisDataLen: 0,
      yAxisDataLen: 0,

      widthScale: STD_WIDTH_SCALE,
      heightScale: STD_HEIGHT_SCALE,

      tooltip: {
        visibility: 'hidden',
        position: {
          x: 0,
          y: 0
        },
        data: [],
        dataName: '',
        legendColor: []
      }
    }
  },
  computed: {
    legendWidth: () => 64,
    topGap: () => FONT_SIZE + 8,
    xChartHeight: () => 215,
    yChartWidth: () => 215,
    xAxisHeight: () => 35,
    displaySpectraWidth () {
      return this.chartWidth - this.legendWidth - this.yAxisWidth - this.yChartWidth
    },
    displaySpectraHeight () {
      return this.chartHeight - this.topGap - this.xAxisHeight - this.xChartHeight
    },
    cacheSpectraWidth () {
      return this.xAxisDataLen * this.widthScale
    },
    cacheSpectraHeight () {
      return this.yAxisDataLen * this.heightScale
    }
  },
  methods: {
    /**
     * 对外接口, 设置雷达图表的显示参数
     * @param chartOpt  {
     *   数据名称
     *   dataName: String,
     *   tooltip: {
     *     xAxisFormatter: Function,
     *     yAxisFormatter: Function,
     *     valueFormatter: Function
     *   }
     *   // 图例配置
     *   legend: {
     *     // 图例最小值
     *     minValue: Number,
     *     // 图例最大值
     *     maxValue: Number,
     *     //          r        g       b
     *     colors: [[Number, Number, Number]]
     *   },
     *   // x轴坐标配置参数
     *   xAxis: {
     *     // x轴坐标名称
     *     name: String,
     *     // 单位
     *     unit: String,
     *     // 是否自适应
     *     fit: Boolean,
     *     // 间隔
     *     interval: Number,
     *     // x轴坐标数据
     *     data: Array<Number, String, Date>,
     *     // x轴坐标显示标签的格式化函数
     *     formatter: Function
     *   },
     *   // y轴坐标配置参数
     *   yAxis: {
     *     // y轴坐标名称
     *     name: String,
     *     // 单位
     *     unit: String,
     *     // 是否自适应
     *     fit: Boolean,
     *     // y轴坐标数据
     *     data: Array<Number, String, Date>,
     *     // y轴坐标显示标签的格式化函数
     *     formatter: Function,
     *   },
     *   // 数据配置
     *   series: {
     *     // 序列名称
     *     name: String,
     *     // 单位
     *     unit: String,
     *     // 数据, 子数组每一个值都为yAxis上的data中对应的值
     *     data: Array<Array<Number>>,
     *     formatter: Function
     *   }
     * }
     * @param clear 是否需要清除
     */
    setOption (chartOpt, clear) {
      if (clear) {
        this.clearDataOption()
      }

      this.chartOpt = merge(this.chartOpt || {}, chartOpt)
      this.tooltip.dataName = this.chartOpt.dataName
      this.setChartSize()
      this.buildLinearColors()
      this.drawChart()
    },

    /**
     * 对外接口, 在已有的数据之前插入数据
     * @param chartDataOpt {
     *   // x轴坐标配置参数
     *   xAxis: {
     *     data: Array<Number, String, Date>
     *   },
     *   // 数据配置
     *   series: [{
     *     // 数据, 子数组每一个值都为yAxis上的data中对应的值
     *     data: values: Array<Array<Number>>
     *   }]
     * }
     */
    insertData (chartDataOpt) {
      this.addData(chartDataOpt, 0)
    },

    /**
     * 对外接口, 在已有的数据之后追加数据
     * @param chartDataOpt {
     *   // x轴坐标配置参数
     *   xAxis: {
     *     // x轴坐标数据
     *     data: Array<Number, String, Date>
     *   },
     *   // 数据配置
     *   series: [{
     *     // 数据, 子数组每一个值都为yAxis上的data中对应的值
     *     data: values: Array<Array<Number>>
     *   }]
     * }
     */
    appendData (chartDataOpt) {
      this.addData(chartDataOpt, 1)
    },

    /**
     * 对外接口, 重置雷达图表尺寸
     */
    resize () {
      this.setChartSize()
      this.drawChart()
      this.moveChartByXY(this.startX, this.startY)
    },

    /**
     * 对外接口, 清空雷达图表
     */
    clear () {
      this.clearAllCanvas()
      this.clearDataOption()
    },

    /**
     * 对外接口, 选中图表上的数据
     * @param xAxisIndex<Number>  x轴数组的下标
     * @param yAxisIndex<Number>  y轴数组的下标
     */
    selectData (xAxisIndex, yAxisIndex) {
      let {
        chartOpt: {
          xAxis: {
            data: xAxisData
          },
          yAxis: {
            data: yAxisData
          },
          series: {
            data: seriesData
          }
        }
      } = this

      let centerX = xAxisIndex * this.widthScale - this.displaySpectraWidth / 2
      let centerY = yAxisIndex * this.heightScale - this.displaySpectraHeight / 2
      this.moveChartByXY(centerX, centerY)

      this.drawSelected({
        area: 3,
        x: xAxisIndex,
        y: yAxisIndex,
        xData: xAxisData[xAxisIndex],
        yData: yAxisData[yAxisIndex],
        value: Array.isArray(seriesData[xAxisIndex]) && Number.isFinite(seriesData[xAxisIndex][yAxisIndex]) ? Number(seriesData[xAxisIndex][yAxisIndex].toFixed(3)) : null,
        selectedData: {
          xValues: seriesData.map(values => Array.isArray(values) ? values[yAxisIndex] : null),
          yValues: seriesData[xAxisIndex]
        }
      })
    },

    /**
     * 内部使用, 设置图表尺寸
     */
    setChartSize () {
      let {chartOpt} = this
      let thisRootEl = this.$el
      let displayCanvas = this.getDisplayChart()

      this.chartWidth = thisRootEl.offsetWidth
      this.chartHeight = thisRootEl.offsetHeight

      if (chartOpt) {
        let {xAxis, yAxis} = chartOpt
        let xAxisData = xAxis.data
        let yAxisData = yAxis.data

        let xAxisDataLen = this.xAxisDataLen = xAxisData.length
        let yAxisDataLen = this.yAxisDataLen = yAxisData.length

        let ctx = displayCanvas.getContext('2d')
        ctx.font = AXIS_FONT
        let maxYAxisLabelWidth = Math.ceil(Math.max(...yAxisData.map(num => ctx.measureText(num).width)))
        this.yAxisWidth = (maxYAxisLabelWidth - ((maxYAxisLabelWidth & 1) ^ 1)) + (MAIN_AXIS_SIZE + 5) * 2

        let {displaySpectraWidth, displaySpectraHeight} = this

        if (xAxis.fit && xAxisDataLen > 0) {
          let n = Math.floor(displaySpectraWidth / xAxisDataLen)
          this.widthScale = Math.max(n, STD_WIDTH_SCALE)

          if (n >= STD_WIDTH_SCALE) {
            this.chartWidth = this.widthScale * xAxisDataLen + this.legendWidth + this.yAxisWidth + this.yChartWidth
          }
        }

        if (yAxis.fit && yAxisDataLen > 0) {
          let n = Math.floor(displaySpectraHeight / yAxisDataLen)
          this.heightScale = Math.max(n, STD_HEIGHT_SCALE)

          if (n >= STD_HEIGHT_SCALE) {
            this.chartHeight = this.heightScale * yAxisDataLen + this.topGap + this.xAxisHeight + this.xChartHeight
          }
        }
      }

      displayCanvas.width = this.chartWidth
      displayCanvas.height = this.chartHeight
    },

    /**
     * 内部使用, 生成渐变色，渲染雷达图谱用
     */
    buildLinearColors () {
      let {colors} = this.chartOpt.legend
      let colorLen = colors.length - 1
      let newColors = this.linearColors = []

      for (let i = 0; i < colorLen; i++) {
        let [r1, g1, b1] = colors[i]
        let [r2, g2, b2] = colors[i + 1]
        let [rStep, gStep, bStep] = [(r2 - r1) / PER_COLOR_STEP, (g2 - g1) / PER_COLOR_STEP, (b2 - b1) / PER_COLOR_STEP]

        for (let j = 0; j < PER_COLOR_STEP; j++) {
          let [r, g, b] = [r1 + j * rStep, g1 + j * gStep, b1 + j * bStep]
          newColors.push([rStep < 0 ? Math.floor(r) : Math.ceil(r), gStep < 0 ? Math.floor(g) : Math.ceil(g), bStep < 0 ? Math.floor(b) : Math.ceil(b)])
        }
      }

      newColors.push(colors[colorLen])
    },

    /**
     * 内部使用, 绘制雷达图表
     */
    drawChart () {
      this.drawLegend()
      this.drawXYChartAxis()
      this.buildSpectraCache()
      this.drawSpectra()
      this.buildSelectedCache()
      this.drawSelectedChart()
      this.buildXChartCache()
      this.drawXChart()
      this.buildYChartCache()
      this.drawYChart()
    },

    /**
     * 内部使用, 画图例
     */
    drawLegend () {
      let {
        chartOpt: {
          legend: {
            minValue,
            maxValue,
            colors
          }
        },
        legendWidth,
        displaySpectraHeight,
        topGap
      } = this
      let colorStep = 1 / (colors.length - 1)
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')
      let lg = ctx.createLinearGradient(0, 0, 0, displaySpectraHeight)

      colors = [...colors].reverse()
      ctx.font = AXIS_FONT

      ctx.clearRect(0, topGap, legendWidth, displaySpectraHeight)

      colors.forEach((color, index) => {
        lg.addColorStop(colorStep * index, `rgb(${color.join(',')})`)
      })

      ctx.fillStyle = lg
      ctx.fillRect(0, topGap, 35, displaySpectraHeight)

      ctx.strokeStyle = LEGEND_BORDER_COLOR
      ctx.strokeRect(0, topGap, 35, displaySpectraHeight)

      ctx.font = AXIS_FONT
      ctx.fillStyle = TEXT_FILL_STYLE
      ctx.textBaseline = 'middle'
      ctx.fillText((maxValue >= 1000 ? (maxValue / 1000 + 'k') : maxValue).toString(), 37, topGap)
      ctx.fillText((minValue >= 1000 ? (minValue / 1000 + 'k') : minValue).toString(), 37, topGap + displaySpectraHeight)
    },

    /**
     * 绘制xyChart的坐标轴
     */
    drawXYChartAxis () {
      let {
        chartOpt: {
          legend: {
            minValue,
            maxValue
          }
        },
        topGap,
        legendWidth,
        displaySpectraWidth,
        displaySpectraHeight,
        xAxisHeight,
        yAxisWidth,
        xChartHeight,
        yChartWidth
      } = this
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')
      let left = legendWidth + displaySpectraWidth
      let yChartBottom = topGap + displaySpectraHeight
      let legendDiff = maxValue - minValue
      let interval = VALUE_INTERVAL - 1
      let valStep = legendDiff / interval

      ctx.clearRect(left, yChartBottom + xAxisHeight, yAxisWidth, xChartHeight)
      ctx.clearRect(left + yAxisWidth, yChartBottom, yChartWidth, xAxisHeight)

      ctx.font = AXIS_FONT
      ctx.fillStyle = TEXT_FILL_STYLE

      // 绘制xChart的y轴
      this.xLine = new ImageData(displaySpectraWidth, xChartHeight)
      let imageData = ctx.getImageData(left, yChartBottom + xAxisHeight, yAxisWidth, xChartHeight)
      let imageColorData = imageData.data
      let heightStep = xChartHeight / interval
      let xLImageData = this.xLine.data

      for (let i = 0; i < xChartHeight; i++) {
        let x = i * yAxisWidth * 4
        imageColorData[x] = BORDER_STYLE[0]
        imageColorData[x + 1] = BORDER_STYLE[1]
        imageColorData[x + 2] = BORDER_STYLE[2]
        imageColorData[x + 3] = 255

        if ((i % heightStep) === 0) {
          for (let j = 1; j < SUB_AXIS_SIZE; j++) {
            let index = x + j * 4
            imageColorData[index] = BORDER_STYLE[0]
            imageColorData[index + 1] = BORDER_STYLE[1]
            imageColorData[index + 2] = BORDER_STYLE[2]
            imageColorData[index + 3] = 255
          }

          if (i !== 0) {
            x = i * displaySpectraWidth * 4

            for (let j = 0; j < displaySpectraWidth; j++) {
              let index = x + j * 4
              xLImageData[index] = SPLIT_LINE_STYLE[0]
              xLImageData[index + 1] = SPLIT_LINE_STYLE[1]
              xLImageData[index + 2] = SPLIT_LINE_STYLE[2]
              xLImageData[index + 3] = 255
            }
          }
        }
      }

      ctx.putImageData(imageData, left, yChartBottom + xAxisHeight - 1)

      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'

      for (let i = 0; i < interval; i++) {
        ctx.fillText(Number((minValue + i * valStep).toFixed(3)).toString(), left + SUB_AXIS_SIZE + 3, (yChartBottom + xAxisHeight) + i * heightStep)
      }

      // 绘制yChart的x轴
      imageData = ctx.getImageData(left + yAxisWidth, yChartBottom, yChartWidth, xAxisHeight)
      imageColorData = imageData.data
      let widthStep = yChartWidth / interval
      this.yLine = new ImageData(yChartWidth, displaySpectraHeight)
      let yLImageData = this.yLine.data

      for (let i = 0; i < yChartWidth; i++) {
        let x = i * 4
        imageColorData[x] = BORDER_STYLE[0]
        imageColorData[x + 1] = BORDER_STYLE[1]
        imageColorData[x + 2] = BORDER_STYLE[2]
        imageColorData[x + 3] = 255

        if ((i % widthStep) === 0) {
          for (let j = 1; j < SUB_AXIS_SIZE; j++) {
            let index = x + j * 4 * yChartWidth
            imageColorData[index] = BORDER_STYLE[0]
            imageColorData[index + 1] = BORDER_STYLE[1]
            imageColorData[index + 2] = BORDER_STYLE[2]
            imageColorData[index + 3] = 255
          }

          if (i !== 0) {
            for (let j = 1; j < displaySpectraHeight; j++) {
              let index = x + j * 4 * yChartWidth
              yLImageData[index] = SPLIT_LINE_STYLE[0]
              yLImageData[index + 1] = SPLIT_LINE_STYLE[1]
              yLImageData[index + 2] = SPLIT_LINE_STYLE[2]
              yLImageData[index + 3] = 255
            }
          }
        }
      }

      ctx.putImageData(imageData, left + yAxisWidth - 1, yChartBottom)

      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'

      for (let i = 0; i < interval; i++) {
        ctx.fillText(Number((minValue + i * valStep).toFixed(3)).toString(), (left + yAxisWidth) + i * heightStep, yChartBottom + SUB_AXIS_SIZE + 3)
      }
    },

    /**
     * 内部使用, 建立雷达图谱缓冲区
     */
    buildSpectraCache () {
      let {
        chartOpt: {
          yAxis: {
            data: yAxisData
          },
          series: {
            data: seriesData
          }
        },
        cacheSpectraWidth,
        cacheSpectraHeight,
        tmpSpectra
      } = this
      let tmpCtx = tmpSpectra.getContext('2d')

      tmpSpectra.width = cacheSpectraWidth
      tmpSpectra.height = cacheSpectraHeight

      tmpCtx.clearRect(0, 0, cacheSpectraWidth, cacheSpectraHeight)
      let imageData = tmpCtx.getImageData(0, 0, cacheSpectraWidth, cacheSpectraHeight)
      this.drawSpectraCache(imageData, seriesData, yAxisData)
      tmpCtx.putImageData(imageData, 0, 0)
    },

    drawSpectraCache (imageData, seriesData, yAxisData) {
      let {width} = imageData
      let {
        chartOpt: {
          legend: {
            minValue,
            maxValue
            // colors: [baseColor]
          }
        },
        linearColors,
        widthScale,
        heightScale
      } = this
      let legendDiff = maxValue - minValue
      let linearColorsLen = linearColors.length - 1
      let imageColorData = imageData.data

      seriesData.forEach((values, xIndex) => {
        if (values === null) {
          yAxisData.forEach((value, yIndex) => {
            for (let x = 0; x < widthScale; x++) {
              let xTmpIndex = (xIndex * widthScale + x) * 4

              for (let y = 0; y < heightScale; y++) {
                let yTmpIndex = (yIndex * heightScale + y) * (width << 2)
                let index = xTmpIndex + yTmpIndex

                // imageColorData[index + 0] = baseColor[0]
                // imageColorData[index + 1] = baseColor[1]
                // imageColorData[index + 2] = baseColor[2]
                imageColorData[index + 0] = 255
                imageColorData[index + 1] = 255
                imageColorData[index + 2] = 255
                imageColorData[index + 3] = 255
              }
            }
          })
        } else {
          values.forEach((value, yIndex) => {
            let convertVal = Math.max(value - minValue, 0)
            let ratio = Math.min(convertVal / legendDiff, 1)
            let color = linearColors[Math.floor(linearColorsLen * ratio)]

            for (let x = 0; x < widthScale; x++) {
              let xTmpIndex = (xIndex * widthScale + x) * 4

              for (let y = 0; y < heightScale; y++) {
                let yTmpIndex = (yIndex * heightScale + y) * (width << 2)
                let index = xTmpIndex + yTmpIndex

                imageColorData[index + 0] = color[0]
                imageColorData[index + 1] = color[1]
                imageColorData[index + 2] = color[2]
                imageColorData[index + 3] = 255
              }
            }
          })
        }
      })
    },

    /**
     * 内部使用, 将缓冲中的雷达图谱数据画到页面画布上
     */
    drawSpectra () {
      let {
        displaySpectraWidth,
        displaySpectraHeight,
        legendWidth,
        topGap
      } = this
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')

      ctx.clearRect(legendWidth, topGap, displaySpectraWidth, displaySpectraHeight)

      ctx.drawImage(
        this.tmpSpectra, // 规定要使用的图像、画布或视频。
        this.startX, this.startY, // 开始剪切的 x 坐标位置。
        displaySpectraWidth, displaySpectraHeight, // 被剪切图像的高度。
        legendWidth, topGap, // 在画布上放置图像的 x 、y坐标位置。
        displaySpectraWidth, displaySpectraHeight // 要使用的图像的宽度、高度
      )
    },

    /**
     * 内部使用, 创建横向图表的缓冲数据
     */
    buildXChartCache (isDrawTrend = true) {
      let {
        chartOpt: {
          xAxis: {
            interval,
            data: xAxisData,
            formatter
          }
        },
        cacheSpectraWidth,
        xChartHeight,
        xAxisHeight,
        widthScale,
        tmpXChart
      } = this
      let tmpCtx = tmpXChart.getContext('2d')
      let totalXChartHeight = xAxisHeight + xChartHeight

      tmpXChart.width = cacheSpectraWidth
      tmpXChart.height = totalXChartHeight
      tmpCtx.font = AXIS_FONT

      tmpCtx.clearRect(0, 0, cacheSpectraWidth, totalXChartHeight)

      let imageData = tmpCtx.getImageData(0, 0, cacheSpectraWidth, totalXChartHeight)
      let imageColorData = imageData.data
      let firstBottomIndex = (xAxisHeight - 1) * cacheSpectraWidth * 4
      let baseStep = cacheSpectraWidth / (xAxisData.length / interval)

      for (let i = 0; i < cacheSpectraWidth; i++) {
        let x = i * 4
        let bottomIndex = x + firstBottomIndex

        imageColorData[x] = imageColorData[bottomIndex] = BORDER_STYLE[0]
        imageColorData[x + 1] = imageColorData[bottomIndex + 1] = BORDER_STYLE[1]
        imageColorData[x + 2] = imageColorData[bottomIndex + 2] = BORDER_STYLE[2]
        imageColorData[x + 3] = imageColorData[bottomIndex + 3] = 255

        if ((i % (baseStep / 4)) === 0) {
          let axisSize = (i % baseStep) === 0 ? MAIN_AXIS_SIZE : SUB_AXIS_SIZE

          for (let j = 1; j < axisSize; j++) {
            let index = x + (j * cacheSpectraWidth * 4)
            let bottomAxisIndex = bottomIndex - (j * cacheSpectraWidth * 4)
            imageColorData[index] = imageColorData[bottomAxisIndex] = BORDER_STYLE[0]
            imageColorData[index + 1] = imageColorData[bottomAxisIndex + 1] = BORDER_STYLE[1]
            imageColorData[index + 2] = imageColorData[bottomAxisIndex + 2] = BORDER_STYLE[2]
            imageColorData[index + 3] = imageColorData[bottomAxisIndex + 3] = 255
          }
        }
      }

      tmpCtx.putImageData(imageData, 0, 0)

      tmpCtx.fillStyle = TEXT_FILL_STYLE
      tmpCtx.textAlign = 'left'
      tmpCtx.textBaseline = 'middle'
      let textPositionY = Math.round(xAxisHeight / 2)

      xAxisData.forEach((value, index) => {
        if ((index % interval) === 0) {
          tmpCtx.fillText(formatter(value), index * widthScale, textPositionY)
          tmpCtx.textAlign = 'center'
        }
      })

      if (isDrawTrend && this.selectedInfo) {
        this.drawXTrend()
      }
    },

    /**
     * 内部使用, 绘制横向图表的曲线图
     */
    drawXTrend () {
      let {
        tmpTrendXChart,
        tmpXChart,
        cacheSpectraWidth,
        xAxisHeight,
        xChartHeight
      } = this
      let trendTop = xAxisHeight

      let tmpCtx = tmpXChart.getContext('2d')
      tmpCtx.clearRect(0, trendTop, cacheSpectraWidth, xChartHeight)

      let tmpTrendCtx = tmpTrendXChart.getContext('2d')
      tmpTrendXChart.width = cacheSpectraWidth
      tmpTrendXChart.height = xChartHeight
      tmpTrendCtx.clearRect(0, 0, cacheSpectraWidth, xChartHeight)

      let {selectedInfo} = this

      if (!selectedInfo) {
        return
      }

      let {xValues} = selectedInfo.selectedData
      let {
        chartOpt: {
          legend: {
            minValue,
            maxValue,
            colors
          }
        },
        widthScale
      } = this
      let grd = tmpTrendCtx.createLinearGradient(0, 0, 0, xChartHeight)
      let legendDiff = maxValue - minValue
      let colorStep = 1 / (colors.length - 1)
      let trendHeight = xChartHeight - LINE_WIDTH
      let method = 'moveTo'

      tmpTrendCtx.imageSmoothingEnabled = true
      tmpTrendCtx.imageSmoothingQuality = 'high'

      colors.forEach((color, index) => {
        grd.addColorStop(index * colorStep, `rgb(${color.join(',')})`)
      })

      tmpTrendCtx.beginPath()

      tmpTrendCtx.lineWidth = LINE_WIDTH
      tmpTrendCtx.strokeStyle = grd

      xValues.forEach((value, index) => {
        if (!Number.isFinite(value)) {
          tmpTrendCtx.stroke()
          tmpTrendCtx.closePath()
          tmpTrendCtx.beginPath()
          method = 'moveTo'
          return
        }

        let convertVal = Math.max(value - minValue, 0)
        let ratio = Math.min(convertVal / legendDiff, 1)
        tmpTrendCtx[method](index * widthScale, ratio * trendHeight)
        method = 'lineTo'
      })

      tmpTrendCtx.stroke()
      tmpTrendCtx.closePath()

      tmpCtx.drawImage(
        tmpTrendXChart, // 规定要使用的图像、画布或视频。
        0, 0, // 开始剪切的 x 坐标位置。
        cacheSpectraWidth, xChartHeight, // 被剪切图像的高度。
        0, trendTop, // 在画布上放置图像的 x 、y坐标位置。
        cacheSpectraWidth, xChartHeight // 要使用的图像的宽度、高度
      )
    },

    /**
     * 内部使用, 绘制横向的图表
     */
    drawXChart () {
      let {xAxisHeight, legendWidth, displaySpectraWidth} = this
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')
      let top = this.topGap + this.displaySpectraHeight
      let totalHeight = xAxisHeight + this.xChartHeight

      ctx.clearRect(legendWidth, top, displaySpectraWidth, totalHeight)
      ctx.putImageData(this.xLine, legendWidth, top + xAxisHeight - 1)

      ctx.drawImage(
        this.tmpXChart, // 规定要使用的图像、画布或视频。
        this.startX, 0, // 开始剪切的 x 坐标位置。
        displaySpectraWidth, totalHeight, // 被剪切图像的高度。
        legendWidth, top, // 在画布上放置图像的 x 、y坐标位置。
        displaySpectraWidth, totalHeight // 要使用的图像的宽度、高度
      )
    },

    /**
     * 内部使用, 创建纵向图表的缓冲数据
     */
    buildYChartCache () {
      let {
        chartOpt: {
          yAxis: {
            interval,
            data: yAxisData
            // formatter
          }
        },
        cacheSpectraHeight,
        yAxisWidth,
        yChartWidth,
        tmpYChart
      } = this
      let tmpCtx = tmpYChart.getContext('2d')
      let totalYChartWidth = yAxisWidth + yChartWidth

      tmpYChart.width = totalYChartWidth
      tmpYChart.height = cacheSpectraHeight
      tmpCtx.font = AXIS_FONT

      tmpCtx.clearRect(0, 0, totalYChartWidth, cacheSpectraHeight)

      let imageData = tmpCtx.getImageData(0, 0, totalYChartWidth, cacheSpectraHeight)
      let imageColorData = imageData.data
      let widthStep = totalYChartWidth * 4
      let xAxisWidthStep = yAxisWidth * 4
      let heightStep = cacheSpectraHeight / ((interval - 1))

      for (let i = 0; i < cacheSpectraHeight; i++) {
        let x = i * widthStep
        let rightX = x + xAxisWidthStep - 4

        imageColorData[x] = imageColorData[rightX] = BORDER_STYLE[0]
        imageColorData[x + 1] = imageColorData[rightX + 1] = BORDER_STYLE[1]
        imageColorData[x + 2] = imageColorData[rightX + 2] = BORDER_STYLE[2]
        imageColorData[x + 3] = imageColorData[rightX + 3] = 255
      }

      let xyStep = heightStep * widthStep

      for (let i = 0; i < interval; i++) {
        let n = Math.ceil(i * xyStep)
        let lIndex = n - (n % widthStep)
        let rIndex = lIndex + xAxisWidthStep

        for (let j = 0; j < SUB_AXIS_SIZE; j++) {
          let tmpLX = lIndex + (j + 1) * 4
          let tmpRX = rIndex - (j + 1) * 4
          imageColorData[tmpLX] = imageColorData[tmpRX] = BORDER_STYLE[0]
          imageColorData[tmpLX + 1] = imageColorData[tmpRX + 1] = BORDER_STYLE[1]
          imageColorData[tmpLX + 2] = imageColorData[tmpRX + 2] = BORDER_STYLE[2]
          imageColorData[tmpLX + 3] = imageColorData[tmpRX + 3] = 255
        }
      }

      tmpCtx.putImageData(imageData, 0, 0)

      let yAxisDataLen = yAxisData.length
      let centerX = Math.round(yAxisWidth / 2)
      let heightValStep = (yAxisDataLen - 1) / (interval - 1)

      tmpCtx.font = AXIS_FONT
      tmpCtx.fillStyle = TEXT_FILL_STYLE
      tmpCtx.textAlign = 'center'
      tmpCtx.textBaseline = 'top'

      for (let i = 0; i < interval; i++) {
        let y = i * heightStep
        tmpCtx.fillText(yAxisData[Math.round(i * heightValStep)], centerX, y)
        tmpCtx.textBaseline = (i + 2) === interval ? 'bottom' : 'middle'
      }

      if (this.selectedInfo) {
        this.drawYTrend()
      }
    },

    /**
     * 内部使用, 绘制横向图表的曲线图
     */
    drawYTrend () {
      let {
        tmpTrendYChart,
        tmpYChart,
        cacheSpectraHeight,
        yAxisWidth,
        yChartWidth
      } = this
      let tmpTrendCtx = tmpTrendYChart.getContext('2d')
      tmpTrendCtx.clearRect(0, 0, yChartWidth, cacheSpectraHeight)
      tmpTrendYChart.width = yChartWidth
      tmpTrendYChart.height = cacheSpectraHeight

      let tmpCtx = tmpYChart.getContext('2d')
      tmpCtx.clearRect(yAxisWidth, 0, yChartWidth, cacheSpectraHeight)

      let {selectedInfo} = this

      if (!selectedInfo) {
        return
      }

      let {yValues} = selectedInfo.selectedData

      if (!Array.isArray(yValues)) {
        return
      }

      let {
        heightScale,
        chartOpt: {
          legend: {
            minValue,
            maxValue,
            colors
          }
        }
      } = this

      let grd = tmpTrendCtx.createLinearGradient(0, 0, yChartWidth, 0)
      let legendDiff = maxValue - minValue
      let colorStep = 1 / (colors.length - 1)
      let trendLeft = yAxisWidth
      let trendWidth = yChartWidth - LINE_WIDTH
      let method = 'moveTo'

      tmpTrendCtx.imageSmoothingEnabled = true
      tmpTrendCtx.imageSmoothingQuality = 'high'

      colors.forEach((color, index) => {
        grd.addColorStop(index * colorStep, `rgb(${color.join(',')})`)
      })

      tmpTrendCtx.beginPath()

      tmpTrendCtx.lineWidth = LINE_WIDTH
      tmpTrendCtx.strokeStyle = grd

      yValues.forEach((value, index) => {
        if (!Number.isFinite(value)) {
          tmpTrendCtx.stroke()
          tmpTrendCtx.closePath()
          tmpTrendCtx.beginPath()
          method = 'moveTo'
          return
        }

        let convertVal = Math.max(value - minValue, 0)
        let ratio = Math.min(convertVal / legendDiff, 1)
        tmpTrendCtx[method](ratio * trendWidth, index * heightScale)
        method = 'lineTo'
      })

      tmpTrendCtx.stroke()
      tmpTrendCtx.closePath()

      tmpCtx.drawImage(
        tmpTrendYChart, // 规定要使用的图像、画布或视频。
        0, 0, // 开始剪切的 x 坐标位置。
        yChartWidth, cacheSpectraHeight, // 被剪切图像的高度。
        trendLeft, 0, // 在画布上放置图像的 x 、y坐标位置。
        yChartWidth, cacheSpectraHeight // 要使用的图像的宽度、高度
      )
    },

    /**
     * 内部使用, 绘制横向的图表
     */
    drawYChart () {
      let {legendWidth, topGap, displaySpectraHeight, yAxisWidth, chartWidth, yChartWidth, chartOpt: {yAxis: {name, unit}}} = this
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')
      let left = legendWidth + this.displaySpectraWidth
      let totalWidth = yAxisWidth + yChartWidth + 1

      ctx.clearRect(left, topGap, totalWidth, displaySpectraHeight)
      ctx.clearRect(legendWidth, 0, chartWidth, topGap - 1)
      ctx.putImageData(this.yLine, chartWidth - yChartWidth - 1, topGap)

      ctx.font = AXIS_FONT
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      // ctx.fillText(`${name}${unit ? `(${unit})` : ''}`, Math.round(left + yAxisWidth / 2), Math.round(topGap / 2))
      ctx.fillText(`${name}${unit && unit.trim() ? `(${unit})` : ''}`, Math.round(left + yAxisWidth / 2), 2)

      ctx.drawImage(
        this.tmpYChart, // 规定要使用的图像、画布或视频。
        0, this.startY, // 开始剪切的 x 坐标位置。
        totalWidth, displaySpectraHeight, // 被剪切图像的高度。
        left, topGap, // 在画布上放置图像的 x 、y坐标位置。
        totalWidth, displaySpectraHeight // 要使用的图像的宽度、高度
      )
    },

    /**
     * 内部使用, 清除所有画布
     */
    clearAllCanvas () {
      let canvases = [
        this.tmpSpectra,
        this.tmpXChart,
        this.tmpTrendXChart,
        this.tmpYChart,
        this.tmpTrendYChart,
        this.tmpSelectedCanvas,
        this.getTooltipCanvas(),
        this.getSelectedCanvas(),
        this.getDisplayChart()
      ]

      canvases.forEach(canvas => {
        canvas.width = canvas.height = 0
      })
    },

    /**
     * 内部使用, 清除数据
     */
    clearDataOption () {
      this.chartOpt = null
      this.selectedInfo = null
      this.startX = this.startY = 0
    },

    /**
     * 内部使用, 清除窗口尺寸变化的事件延迟
     */
    clearResizeTimer () {
      clearTimeout(this.windowResizeTimer)
    },

    /**
     * 内部使用, 当窗口尺寸发生变化时的事件
     */
    onWindowResize () {
      this.clearResizeTimer()
      this.windowResizeTimer = setTimeout(this.resize, 100)
    },

    /**
     * 判断当前坐标是否在雷达图谱上
     * @param x 横坐标
     * @param y 纵坐标
     */
    isNotInSpectra (x, y) {
      let {
        legendWidth,
        displaySpectraWidth,
        topGap,
        displaySpectraHeight
      } = this
      return x < legendWidth || x >= (legendWidth + displaySpectraWidth) || y < topGap || y >= (topGap + displaySpectraHeight)
    },

    /**
     * 判断当前坐标是否在xChart上
     * @param x 横坐标
     * @param y 纵坐标
     */
    isNotInXChart (x, y) {
      let {
        legendWidth,
        displaySpectraWidth,
        topGap,
        displaySpectraHeight,
        xAxisHeight,
        xChartHeight
      } = this
      let top = topGap + displaySpectraHeight + xAxisHeight
      return x < legendWidth || x >= (legendWidth + displaySpectraWidth) || y < top || y >= (top + xChartHeight)
    },

    /**
     * 判断当前坐标是否在yChart上
     * @param x 横坐标
     * @param y 纵坐标
     */
    isNotInYChart (x, y) {
      let {
        legendWidth,
        displaySpectraWidth,
        yAxisWidth,
        yChartWidth,
        topGap,
        displaySpectraHeight
      } = this
      let left = legendWidth + displaySpectraWidth + yAxisWidth
      return x < left || x >= (left + yChartWidth) || y < topGap || y >= (topGap + displaySpectraHeight)
    },

    /**
     * 内部使用, 获取当前图表上选择到的数据
     */
    getDataByEvent (event) {
      let {layerX, layerY, type} = event
      let {
        topGap,
        legendWidth,
        startX,
        startY,
        widthScale,
        heightScale,
        chartOpt: {
          xAxis: {
            data: xAxisData
          },
          yAxis: {
            data: yAxisData
          },
          series: {
            data: seriesData
          }
        }
      } = this
      let isNotInSpectra = this.isNotInSpectra(layerX, layerY)
      let selectedInfo = {}
      let x
      let y

      if (type === 'dblclick') {
        if (isNotInSpectra) {
          return null
        }

        x = Math.floor((layerX - legendWidth + startX) / widthScale)
        y = Math.floor((layerY - topGap + startY) / heightScale)

        selectedInfo.area = 3
        selectedInfo.selectedData = {
          xValues: seriesData.map(values => Array.isArray(values) ? values[y] : null),
          yValues: seriesData[x]
        }
      } else if (isNotInSpectra) {
        let currentSelectedInfo = this.selectedInfo

        if (!currentSelectedInfo) {
          return null
        }

        if (this.isNotInXChart(layerX, layerY)) {
          if (this.isNotInYChart(layerX, layerY)) {
            return null
          }

          x = currentSelectedInfo.x
          y = Math.floor((layerY - topGap + startY) / heightScale)
          selectedInfo.area = 2
        } else {
          x = Math.floor((layerX - legendWidth + startX) / widthScale)
          y = currentSelectedInfo.y
          selectedInfo.area = 1
        }
      } else {
        selectedInfo.area = 3
        x = Math.floor((layerX - legendWidth + startX) / widthScale)
        y = Math.floor((layerY - topGap + startY) / heightScale)
      }

      selectedInfo.x = x
      selectedInfo.y = y
      selectedInfo.xData = xAxisData[x]
      selectedInfo.yData = yAxisData[y]
      selectedInfo.value = Array.isArray(seriesData[x]) && Number.isFinite(seriesData[x][y]) ? Number(seriesData[x][y].toFixed(3)) : null

      return selectedInfo
    },

    /**
     * 内部使用, 当双击图表时的事件
     */
    onChartDBLClick (event) {
      let data = this.getDataByEvent(event, true)

      if (data) {
        this.drawSelected(data)
        this.$emit(LIDAR_CHART_DATA_SELECTED, data)
      }
    },

    /**
     * 图表鼠标右键事件
     */
    onChartContextMenu (event) {
      let {selectedInfo} = this

      if (!selectedInfo) {
        return
      }

      this.drawSelected(null)
      this.$emit(LIDAR_CHART_DATA_CANCEL_SELECT, selectedInfo)
    },

    drawSelected (selectedInfo) {
      this.selectedInfo = selectedInfo
      this.drawXTrend()
      this.drawXChart()
      this.drawYTrend()
      this.drawYChart()
      this.buildSelectedCache()
      this.drawSelectedChart()
    },

    /**
     * 当鼠标左键按下时的事件
     */
    onChartMouseDown (event) {
      let {layerX, layerY} = event

      if (this.isNotInSpectra(layerX, layerY)) {
        return
      }

      this.oX = layerX
      this.oY = layerY
      this.sX = this.startX
      this.sY = this.startY
      this.isMoving = true
    },

    /**
     * 鼠标移动时的事件
     */
    onChartMouseMove (event) {
      if (this.isMoving) {
        this.tooltip.visibility = 'hidden'
        this.clearTooltipBorder()
        this.moveChart(event)
      } else {
        this.showTooltip(event)
      }
    },

    /**
     * 当鼠标松开时的事件
     */
    onChartMouseUp (event) {
      this.isMoving = false
    },

    /**
     * 鼠标移出画布事件
     */
    onChartMouseOut (event) {
      if (this.isMoving) {
        this.onChartMouseUp()
      } else {
        this.tooltip.visibility = 'hidden'
        this.clearTooltipBorder()
      }
    },

    onChartMouseWheel (event) {
      let data = this.getDataByEvent(event)

      if (data && (data.area & 3) === 3) {
        let {deltaX, deltaY} = event
        this.tooltip.visibility = 'hidden'
        this.clearTooltipBorder()
        this.moveChartByXY(this.startX + deltaX, this.startY + deltaY)
        this.emitLidarChartMoved(-deltaX, -deltaX)
        event.preventDefault()
      }
    },

    /**
     * 移动图表
     */
    moveChart (event) {
      let {layerX, layerY} = event
      let xDiff = layerX - this.oX
      let yDiff = layerY - this.oY
      let startX = this.sX - xDiff
      let startY = this.sY - yDiff
      this.moveChartByXY(startX, startY)
      this.emitLidarChartMoved(xDiff, yDiff)
    },

    moveChartByXY (startX, startY) {
      let isMove = 3
      let {tmpSpectra} = this

      if (startX < 0) {
        startX = 0
      } else {
        let {displaySpectraWidth} = this
        let cacheSpectraWidth = tmpSpectra.width

        if ((startX + displaySpectraWidth) > cacheSpectraWidth) {
          startX = cacheSpectraWidth - displaySpectraWidth
        }
      }

      if (startX === this.startX) {
        isMove ^= 1
      }

      if (startY < 0) {
        startY = 0
      } else {
        let {displaySpectraHeight} = this
        let cacheSpectraHeight = tmpSpectra.height

        if ((startY + displaySpectraHeight) > cacheSpectraHeight) {
          startY = cacheSpectraHeight - displaySpectraHeight
        }
      }

      if (startY === this.startY) {
        isMove ^= 2
      }

      this.startX = startX
      this.startY = startY

      if (isMove) {
        this.drawSpectra()
        this.drawSelectedChart()
      }

      if (isMove & 1) {
        this.drawXChart()
      }

      if (isMove & 2) {
        this.drawYChart()
      }
    },

    emitLidarChartMoved (xDiff, yDiff) {
      let {
        chartOpt: {
          xAxis: {
            data: xAxisData
          },
          yAxis: {
            data: yAxisData
          }
        },
        startX,
        startY,
        widthScale,
        heightScale,
        displaySpectraWidth,
        displaySpectraHeight
      } = this
      let beginX = Math.floor(startX / widthScale)
      let beginY = Math.floor(startY / heightScale)
      let endX = Math.ceil((startX + displaySpectraWidth - widthScale) / widthScale)
      let endY = Math.ceil((startY + displaySpectraHeight - heightScale) / heightScale)
      this.$emit(LIDAR_CHART_MOVED, {
        beginXAxisIndex: beginX,
        beginXAxisValue: xAxisData[beginX],
        beginYAxisIndex: beginY,
        beginYAxisValue: yAxisData[beginY],
        endXAxisIndex: endX,
        endXAxisValue: xAxisData[endX],
        endYAxisIndex: endY,
        endYAxisValue: yAxisData[endY],
        directX: (xDiff >> -1) | 1,
        directY: (yDiff >> -1) | 1
      })
    },

    /**
     * 显示当前鼠标所在区域数据信息
     */
    showTooltip (event) {
      let {chartOpt} = this

      if (!chartOpt) {
        return
      }

      let data = this.getDataByEvent(event, true)
      let {tooltip, chartOpt: {tooltip: tooltipOpt}} = this

      if (!data) {
        tooltip.visibility = 'hidden'
        this.clearTooltipBorder()
        return
      }

      let {area} = data
      let tooltipData = []

      if (area & 1) {
        let {xAxis} = chartOpt
        tooltipData.push({
          label: xAxis.name,
          value: tooltipOpt.xAxisFormatter(data.xData)
        })
      }

      if (area & 2) {
        let {yAxis} = chartOpt
        tooltipData.push({
          label: yAxis.name,
          value: tooltipOpt.yAxisFormatter(data.yData)
        })
      }

      let {value} = data

      if (Number.isFinite(value)) {
        let {legend: {minValue, maxValue}} = chartOpt
        let {linearColors} = this
        let convertVal = Math.max(value - minValue, 0)
        let ratio = Math.min(convertVal / (maxValue - minValue), 1)
        tooltip.legendColor = [...linearColors[Math.floor((linearColors.length - 1) * ratio)]]

        tooltipData.push({
          label: chartOpt.series.name,
          value: tooltipOpt.valueFormatter(value)
        })
      } else {
        tooltip.legendColor = [255, 255, 255]

        tooltipData.push({
          label: chartOpt.series.name,
          value: '--'
        })
      }

      let {layerX, layerY} = event
      tooltip.data = tooltipData
      tooltip.position = {
        x: layerX + 10,
        y: layerY + 10
      }
      tooltip.visibility = 'visible'
      this.drawTooltip(layerX, layerY, area)
    },

    /**
     * 清除tooltip的线条
     */
    clearTooltipBorder () {
      let tooltipCanvas = this.getTooltipCanvas()
      let ctx = tooltipCanvas.getContext('2d')
      ctx.clearRect(0, 0, this.chartWidth, this.chartHeight)
    },

    /**
     * 画辅助线
     * @param x 横坐标
     * @param y 纵坐标
     */
    drawTooltip (x, y, area) {
      let {
        chartWidth,
        chartHeight,
        selectedInfo
      } = this

      this.clearTooltipBorder()
      let tooltipCanvas = this.getTooltipCanvas()
      let ctx = tooltipCanvas.getContext('2d')
      tooltipCanvas.width = chartWidth
      tooltipCanvas.height = chartHeight

      let imageData = ctx.getImageData(0, 0, chartWidth, chartHeight)
      let imageColorData = imageData.data

      if (area & 1) {
        this.drawLine(imageColorData, (x + this.topGap * chartWidth) * 4, this.displaySpectraHeight, chartWidth, 7, TOOLTIP_BORDER_STYLE)

        if (selectedInfo) {
          let {xChartHeight} = this
          this.drawLine(imageColorData, (x + (chartHeight - xChartHeight) * chartWidth) * 4, xChartHeight, chartWidth, 7, TOOLTIP_BORDER_STYLE)
        }
      }

      if (area & 2) {
        this.drawLine(imageColorData, (this.legendWidth + y * chartWidth) * 4, this.displaySpectraWidth, 1, 7, TOOLTIP_BORDER_STYLE)

        if (selectedInfo) {
          let {yChartWidth} = this
          this.drawLine(imageColorData, (chartWidth - yChartWidth + y * chartWidth) * 4, yChartWidth, 1, 7, TOOLTIP_BORDER_STYLE)
        }
      }

      ctx.putImageData(imageData, 0, 0)
    },

    /**
     * 画出线条
     * @param imageColorData<Array<Number>> 颜色数组
     * @param startColorIndex<Number>       线条的起始位置的下标
     * @param len<Number>                   线条的长度
     * @param step<Number>                  方向  横线：1, 纵线: 颜色数组的宽
     * @param lineStep<Number>              虚线的长度
     * @param color<Array<Number>>          线条颜色
     */
    drawLine (imageColorData, startColorIndex, len, step, lineStep, color) {
      let maxMod = lineStep - 1

      for (let i = 0; i < len; i++) {
        let mod = (i + 1) % lineStep

        if (mod === 0 || mod === maxMod) {
          continue
        }

        let index = startColorIndex + i * 4 * step
        imageColorData[index] = TOOLTIP_BORDER_STYLE[0]
        imageColorData[index + 1] = TOOLTIP_BORDER_STYLE[1]
        imageColorData[index + 2] = TOOLTIP_BORDER_STYLE[2]
        imageColorData[index + 3] = 255
      }
    },

    buildSelectedCache () {
      let {tmpSelectedCanvas, cacheSpectraWidth, cacheSpectraHeight, selectedInfo} = this
      let tmpCtx = tmpSelectedCanvas.getContext('2d')
      tmpSelectedCanvas.width = cacheSpectraWidth
      tmpSelectedCanvas.height = cacheSpectraHeight

      tmpCtx.clearRect(0, 0, cacheSpectraWidth, cacheSpectraHeight)

      if (!selectedInfo) {
        return
      }

      let x = selectedInfo.x * this.widthScale
      let y = selectedInfo.y * this.heightScale
      let imageData = tmpCtx.getImageData(0, 0, cacheSpectraWidth, cacheSpectraHeight)
      let imageColorData = imageData.data

      this.drawLine(imageColorData, y * cacheSpectraWidth * 4, cacheSpectraWidth, 1, cacheSpectraWidth + 1, BORDER_STYLE)
      this.drawLine(imageColorData, x * 4, cacheSpectraHeight, cacheSpectraWidth, cacheSpectraHeight + 1, BORDER_STYLE)

      tmpCtx.putImageData(imageData, 0, 0)
    },

    drawSelectedChart () {
      let {
        chartWidth,
        chartHeight,
        displaySpectraWidth,
        displaySpectraHeight,
        legendWidth,
        topGap
      } = this
      let selectedCanvas = this.getSelectedCanvas()
      let ctx = selectedCanvas.getContext('2d')

      ctx.clearRect(0, 0, chartWidth, chartHeight)
      selectedCanvas.width = chartWidth
      selectedCanvas.height = chartHeight

      ctx.drawImage(
        this.tmpSelectedCanvas, // 规定要使用的图像、画布或视频。
        this.startX, this.startY, // 开始剪切的 x 坐标位置。
        displaySpectraWidth, displaySpectraHeight, // 被剪切图像的高度。
        legendWidth, topGap, // 在画布上放置图像的 x 、y坐标位置。
        displaySpectraWidth, displaySpectraHeight // 要使用的图像的宽度、高度
      )
    },

    /**
     * 添加雷达数据
     */
    addData (chartDataOpt, direction) {
      let {chartOpt, tmpSpectra, cacheSpectraHeight} = this
      let {
        xAxis,
        yAxis: {
          data: yAxisData
        },
        series
      } = chartOpt
      let {
        xAxis: {
          data: xAxisData
        },
        series: {
          data: seriesData
        }
      } = chartDataOpt
      let tmpCtx = tmpSpectra.getContext('2d')
      let {width, height} = tmpSpectra
      let oldImageData = tmpCtx.getImageData(0, 0, width, height)
      let imageData = new ImageData(xAxisData.length * this.widthScale, cacheSpectraHeight)
      let newXAxisData = new Array(2)
      let newSeriesData = new Array(2)
      let newImageData = new Array(2)

      newXAxisData[direction] = xAxisData
      newSeriesData[direction] = seriesData
      newImageData[direction] = imageData
      direction ^= 1
      newXAxisData[direction] = xAxis.data
      newSeriesData[direction] = series.data
      newImageData[direction] = oldImageData
      xAxis.data = newXAxisData.flat()
      series.data = newSeriesData.flat()

      this.xAxisDataLen = xAxis.data.length
      let {cacheSpectraWidth} = this

      tmpSpectra.width = cacheSpectraWidth
      tmpSpectra.height = cacheSpectraHeight

      tmpCtx.clearRect(0, 0, cacheSpectraWidth, cacheSpectraHeight)
      this.drawSpectraCache(imageData, seriesData, yAxisData)
      tmpCtx.putImageData(newImageData[0], 0, 0)
      tmpCtx.putImageData(newImageData[1], newImageData[0].width, 0)

      let add = -direction & imageData.width
      this.startX += add
      this.sX += add

      let {selectedInfo} = this

      if (selectedInfo) {
        let {
          y,
          selectedData
        } = selectedInfo

        selectedInfo.x += -direction & newXAxisData[0].length
        let newSelectedDataXValues = new Array(2)
        newSelectedDataXValues[direction] = selectedData.xValues
        newSelectedDataXValues[direction ^ 1] = seriesData.map(values => Array.isArray(values) ? values[y] : null)
        selectedData.xValues = newSelectedDataXValues.flat()
      }

      this.buildXChartCache()
      this.buildSelectedCache()
      this.drawSelectedChart()
    },

    onDownloadBtnClick () {
      let {
        tmpSpectra,
        yAxisWidth,
        xAxisHeight,
        selectedInfo,
        legendWidth,
        yChartWidth,
        topGap,
        xChartHeight,
        displaySpectraHeight
      } = this
      let displayCanvas = this.getDisplayChart()
      let interceptionYChartWidth = yAxisWidth
      let interceptionXChartHeight = xAxisHeight
      let {width, height} = tmpSpectra
      let downloadCanvas = document.createElement('canvas')
      let downloadCtx = downloadCanvas.getContext('2d')

      if (selectedInfo) {
        interceptionYChartWidth += yChartWidth
        interceptionXChartHeight += xChartHeight

        this.$nextTick(() => {
          // 绘制xyChart的坐标轴
          downloadCtx.drawImage(
            displayCanvas,
            displayCanvas.width - yChartWidth - yAxisWidth, topGap + displaySpectraHeight,
            interceptionYChartWidth, interceptionXChartHeight,
            legendWidth + width, topGap + height,
            interceptionYChartWidth, interceptionXChartHeight
          )

          // 绘制yChart的splitLine
          downloadCtx.putImageData(
            this.getSplitLineImageData([yChartWidth, height], 1),
            legendWidth + width + yAxisWidth - 1, topGap
          )

          downloadCtx.putImageData(
            this.getSplitLineImageData([width, xChartHeight], 0),
            legendWidth, topGap + height + xAxisHeight - 1
          )

          this.$nextTick(() => {
            // 绘制选中数据时的线
            downloadCtx.drawImage(
              this.tmpSelectedCanvas,
              0, 0,
              width, height,
              legendWidth, topGap,
              width, height
            )
          })
        })
      }

      let totalWidth = downloadCanvas.width = legendWidth + width + interceptionYChartWidth
      downloadCanvas.height = topGap + height + interceptionXChartHeight
      downloadCtx.fillStyle = '#ffffff'
      downloadCtx.fillRect(0, 0, totalWidth, downloadCanvas.height)

      this.$nextTick(() => {
        // 绘制图例
        downloadCtx.drawImage(
          displayCanvas,
          0, 0,
          legendWidth, displayCanvas.height,
          0, 0,
          legendWidth, displayCanvas.height
        )

        // 绘制图谱
        downloadCtx.drawImage(
          tmpSpectra,
          0, 0,
          width, height,
          legendWidth, topGap,
          width, height
        )

        // 绘制Y轴名称单位
        let yAxisNameUnitWidth = this.displaySpectraWidth + interceptionYChartWidth
        downloadCtx.drawImage(
          displayCanvas,
          legendWidth, 0,
          yAxisNameUnitWidth, topGap - 1,
          totalWidth - yAxisNameUnitWidth, 0,
          yAxisNameUnitWidth, topGap - 1
        )

        // 绘制yChart
        downloadCtx.drawImage(
          this.tmpYChart,
          0, 0,
          interceptionYChartWidth, height,
          legendWidth + width, topGap,
          interceptionYChartWidth, height
        )

        // 绘制xChart
        downloadCtx.drawImage(
          this.tmpXChart,
          0, 0,
          width, interceptionXChartHeight,
          legendWidth, topGap + height,
          width, interceptionXChartHeight
        )

        this.$nextTick(() => {
          downloadCanvas.toBlob(blob => {
            let url = URL.createObjectURL(blob)
            let link = document.createElement('a')
            link.download = `雷达图.${/\/([^/]+)$/.exec(blob.type)[1]}`
            link.href = url
            link.click()
            URL.revokeObjectURL(url)
            downloadCanvas.width = downloadCanvas.height = 0
          })
        })
      })
    },

    /**
     * 获取横向或纵向的chart分割线
     * @param size<Array<Number>> 尺寸
     * @param direction<Number>   方向: 0.横向  1.纵向
     */
    getSplitLineImageData (size, direction) {
      let interval = VALUE_INTERVAL - 1
      let [width, height] = size
      let len = size[direction]
      let nextDirection = direction ^ 1
      let rowStep = size[nextDirection] / interval
      let steps = new Array(2)

      steps[direction] = width
      steps[nextDirection] = 1

      let [xStep, yStep] = steps
      let imageData = new ImageData(width, height)
      let imageColorData = imageData.data

      imageColorData.fill(255)

      for (let i = 1; i < VALUE_INTERVAL; i++) {
        let rowIndex = i * rowStep * 4 * xStep
        rowIndex -= rowIndex % 4

        for (let j = 0; j < len; j++) {
          let index = rowIndex + j * 4 * yStep

          imageColorData[index] = SPLIT_LINE_STYLE[0]
          imageColorData[index + 1] = SPLIT_LINE_STYLE[1]
          imageColorData[index + 2] = SPLIT_LINE_STYLE[2]
          imageColorData[index + 3] = 255
        }
      }

      return imageData
    },

    /**
     * 内部使用, 获取当前图表画布
     * @returns {Vue | Element | Vue[] | Element[]}
     */
    getDisplayChart () {
      return this.$refs['lidar-chart']
    },

    getTooltipCanvas () {
      return this.$refs['tooltip-canvas']
    },

    getSelectedCanvas () {
      return this.$refs['selected-canvas']
    }
  },
  created () {
    this.tmpSpectra = document.createElement('canvas')
    this.tmpXChart = document.createElement('canvas')
    this.tmpTrendXChart = document.createElement('canvas')
    this.tmpYChart = document.createElement('canvas')
    this.tmpTrendYChart = document.createElement('canvas')
    this.tmpSelectedCanvas = document.createElement('canvas')
    window.addEventListener('resize', this.onWindowResize, true)
  },
  mounted () {
    this.$nextTick(() => {
      this.$emit(LIDAR_CHART_READY, this)
    })
  },
  beforeDestroy () {
    this.$emit(LIDAR_CHART_DESTROY, this)
    this.clear()
    this.clearResizeTimer()
    window.removeEventListener('resize', this.onWindowResize, true)
  }
}
</script>

<style lang="scss" scoped>
.lidar-chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;

  > {
    .lidar-chart-inner {
      position: relative;
    }

    .tooltip {
      position: absolute;
      font-size: .875rem;
      min-width: 125px;
      /*width: 175px;*/
      padding: 5px 10px;
      line-height: 1.5;
      border: 1px solid #DCDFE6 {
        radius: 3px;
      };
      color: #606266;
      white-space: nowrap;
      background-color: #ffffff;
      box-shadow: 0 0 6px 1px rgba(0, 0, 0, .075);
      user-select: none;
      z-index: 2;

      .legend-marker {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 4px;
        vertical-align: middle;
      }

      .tooltip-value {
        float: right;
        text-indent: 1em;
      }
    }

    .download-btn {
      position: absolute;
      font-size: 13px;
      top: 0;
      right: 10px;
      color: #606266;
      cursor: pointer;

      &:hover {
        color: #409EFF;
        text-decoration: underline;
      }
    }
  }

  .tooltip-canvas,
  .selected-canvas {
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Data/Charts/LidarChart/index.vue